import {
  AfterViewInit,
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  input,
  signal,
} from '@angular/core';
import { SurveyRenderControllerStore } from '../render-store/render.store';
import { BaseComposedElement } from '@hyper-inferno/survey-elements-core';
import { coerecedPayloadBuilder } from './coerced-payload.util';
declare const $: any;

// 1. Define a helper that extracts the config type based on the 'type' value
type ExtractConfig<T> = T extends { type: infer K }
  ? K extends keyof T
    ? T[K]
    : never
  : never;

@Component({ template: '' }) // Base classes need a template-less component decorator
export abstract class BaseQuestionComponent<
  TComposedConfig extends BaseComposedElement & Record<string, any>,
> implements AfterViewInit
{
  protected el = inject(ElementRef);
  protected store = inject(SurveyRenderControllerStore);
  // 1. Mandatory Input with Type Safety
  question = input.required<TComposedConfig>();

  /** * We use a 'Pulse' object to track legacy changes.
   * Every time we call onValueChange() without a value,
   * we increment the version to force reactivity.
   */
  protected legacyPulse = signal<{ version: number }>({ version: 0 });
  // 2. Computed Config: Extract the specific config based on the question type
  // This replaces: const q = this.question(); return q?.[q.type];
  config = computed(() => {
    const q = this.question();
    const typeKey = q.type as keyof TComposedConfig;
    return q[typeKey] as ExtractConfig<TComposedConfig>;
    // return q[q.type];
  });

  // 3. Common UI State
  validationError = signal<string | null>(null);
  protected customValue = signal<any>(undefined);

  // The Serializer: Automatically runs whenever rawValue changes
  coercedValue = computed(() => {
    const pulse = this.legacyPulse();
    return this.coerce(this.customValue());
  });

  constructor() {
    // Automatically sync this component's error state to the central store
    effect(() => {
      // 1. Watch for global submit attempts
      const attempt = this.store.submitAttempted();

      // 2. If a submit was attempted, force run validation

      const id = this.question().id;
      const error = this.validationError();
      const data = this.coercedValue();
      const isValid = error === null;
      this.store.updateQuestionState(
        String(id) as string,
        data,
        isValid,
        error,
        () => {
          const valid = this.validate();
          return valid;
        },
      );
    });
  }
  ngAfterViewInit(): void {
    this.hydrate(this.question().answerObject);
  }

  // 4. Abstract Method: Forces every child (OpenEnded, Radio, etc.)
  // to implement its own specific validation logic.
  abstract validate(): boolean;

  /**
   * HYDRATION: The bridge from the Store back to the Component.
   */
  hydrate(answerObj: any) {
    // 1. Get the slice of data belonging to this question
    // Note: Use question().answerObject if provided, otherwise filter the global store object
    const dataToHydrate = answerObj || this.question().answerObject || {};

    if (Object.keys(dataToHydrate).length === 0) return;

    // 2. Determine Mode and Update Local Signal
    // We let the customHydrate method return what the rawValue signal should hold
    const processedValue = this.customHydrate(dataToHydrate);
    this.customValue.set(processedValue);

    // 3. Legacy Fallback: If processedValue is null, it means we are in Legacy Mode.
    // We only run the jQuery logic if we AREN'T handling this via a custom signal.
    if (processedValue === null) {
      const $root = $(this.el.nativeElement);

      $.each(dataToHydrate, (key: string, value: any) => {
        let $field = $root.find('[name="' + key + '"]');
        if (!$field.length) {
          $field = $root.find('[name="' + key + '[]"]');
          if (!$field.length) return;
        }

        if ($field.is(':checkbox')) {
          $field.prop('checked', !!value);
        } else if ($field.is(':radio')) {
          $field.filter(`[value="${value}"]`).prop('checked', true);
        } else if ($field.is('select')) {
          $field.val(value).change();
        } else if (
          $field.is('input[type="text"], textarea, input[type="number"]')
        ) {
          $field.val(value);
        } else {
          $field.val(value);
        }
      });
    }

    // Always validate after hydration to ensure the Store registry is accurate
    this.validate();
  }

  /**
   * OVERRIDE THIS: To transform flat Bot data into your component's Signal state.
   * Return 'null' (default) to trigger Legacy jQuery hydration.
   */
  protected customHydrate(data: any): any {
    return null;
  }

  private coerce(val: any): any {
    let payload: any;

    if (val !== null && val !== undefined) {
      // CUSTOM MODE: User has provided a signal value
      payload = this.customCoerce(val);
    } else {
      // LEGACY MODE: Scrape the DOM
      const $root = $(this.el.nativeElement);
      payload = $root.find('input, select, textarea').serializeJSON();
    }

    // Always ensure the naming is correct: if it's a primitive, wrap it
    if (typeof payload !== 'object' || Array.isArray(payload)) {
      payload = { [this.question().name]: payload };
    }

    // Pass to your AJV Utility for final type casting (string -> int etc)
    return coerecedPayloadBuilder(payload, this.question() as any);
  }

  // Helper to clear errors
  protected clearError() {
    this.validationError.set(null);
  }

  /**
   * OVERRIDE THIS: For non-legacy components (like Ranking).
   * By default, it just returns the value as-is.
   */
  protected customCoerce(val: any): any {
    throw new Error(
      `Component "${this.constructor.name}" provided a customValue to onValueChange(), ` +
        `but did not override customCoerce() to handle it.`,
    );
  }

  // /**
  //  * Called by UI events. Triggers the whole sync process.
  //  */
  // protected onValueChange(value: any) {
  //   this.rawValue.set(value);
  //   this.validate();
  // }
  /**
   * Called by UI events.
   * Pass a value to trigger Custom Mode.
   * Call without arguments to trigger Legacy Scrape.
   */
  protected onValueChange(value?: any) {
    if (value !== 'LEGACY') {
      this.customValue.set(value);
    } else {
      // LEGACY KICK: Increment version to ensure the computed signal is dirty
      this.legacyPulse.update((p) => ({ version: p.version + 1 }));
    }
    this.validate();
  }
}
